/**
 * useMetrics — Dashboard metrics hook using unified EventStream
 *
 * Receives real-time metrics updates via the shared SSE connection.
 * Falls back to REST API if SSE is unavailable.
 *
 * Usage:
 *   const { data, connected, loading, refresh } = useMetrics({
 *     categories: ['users', 'samples', 'system'],
 *     timeRange: '30d',
 *   });
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useEventStreamContext, CHANNELS } from "context/EventStreamContext";
import { useAuth } from "context/AuthContext";
import useApi from "hooks/useApi";
import { endpoints } from "config/api";

export default function useMetrics(options = {}) {
  const {
    categories = ["users", "samples", "system"],
    timeRange = "7d",
    autoConnect = true,
  } = options;

  const { currentUser } = useAuth();
  const { get } = useApi();
  const { connected: streamConnected, subscribe, activeChannels } = useEventStreamContext();

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Stabilize categories to avoid infinite re-render loops
  // (callers pass .map() which creates a new array every render)
  const categoriesKey = categories.join(",");

  // Check if we're subscribed to metrics channel
  const isMetricsSubscribed = useMemo(
    () => activeChannels.includes(CHANNELS.METRICS),
    [activeChannels]
  );

  // Fetch snapshot via REST (fallback or initial load)
  const fetchSnapshot = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        categories: categoriesKey,
        timeRange,
      });
      const result = await get(`${endpoints.metrics.snapshot}?${params}`);
      if (result.data) {
        setData(result.data);
        setLastUpdate(Date.now());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, get, categoriesKey, timeRange]);

  // Keep a ref so the subscription effect doesn't depend on fetchSnapshot
  const fetchSnapshotRef = useRef(fetchSnapshot);
  fetchSnapshotRef.current = fetchSnapshot;

  // Handle incoming metric events from EventStream
  const handleMetricsEvent = useCallback((event) => {
    if (event.type === "snapshot" || event.type === "update") {
      setData(event.data);
      setLastUpdate(Date.now());
      setLoading(false);
    } else if (event.type === "data") {
      if (event.data.category && event.data.data) {
        setData((prev) => ({
          ...prev,
          [event.data.category]: event.data.data,
        }));
        setLastUpdate(Date.now());
      }
    }
  }, []);

  // Subscribe to metrics channel (stable deps — won't cause loops)
  useEffect(() => {
    if (!currentUser || !autoConnect) {
      setData({});
      return;
    }

    const unsubscribe = subscribe(CHANNELS.METRICS, handleMetricsEvent);

    return () => {
      unsubscribe();
    };
  }, [currentUser, autoConnect, subscribe, handleMetricsEvent]);

  // Fetch REST snapshot when SSE isn't subscribed yet
  useEffect(() => {
    if (!currentUser || !autoConnect) return;
    if (!isMetricsSubscribed) {
      fetchSnapshotRef.current();
    }
  }, [currentUser, autoConnect, isMetricsSubscribed]);

  // Refresh metrics data
  const refresh = useCallback(() => {
    fetchSnapshotRef.current();
  }, []);

  return useMemo(
    () => ({
      data,
      // Use the raw stream connected state — metrics is always requested when this hook is active
      connected: streamConnected,
      loading,
      error,
      lastUpdate,
      refresh,
      // Individual metric categories for convenience
      users: data.users || null,
      samples: data.samples || null,
      system: data.system || null,
    }),
    [data, streamConnected, loading, error, lastUpdate, refresh]
  );
}
